'use client';

import React from 'react';
import { useRouter } from 'next/navigation';

interface OrderConfirmationProps {
  orderNumber: string;
  estimatedTime: string;
  phoneNumber: string;
  onClose: () => void;
}

export default function OrderConfirmation({
  orderNumber,
  estimatedTime,
  phoneNumber,
  onClose,
}: OrderConfirmationProps) {
  const router = useRouter();

  const goHome = () => {
    onClose();
    router.push('/home-landing');
  };

  const backToMenu = () => {
    onClose();
    router.push('/menu-catalog');
  };

  return (
    <div className="fixed inset-0 z-300 flex items-center justify-center bg-black/40 px-4">
      <div className="relative w-full max-w-lg rounded-2xl bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h3 className="font-heading text-lg font-semibold text-foreground">Order Confirmed!</h3>
          <button
            type="button"
            className="rounded-lg px-2 py-1 text-muted-foreground hover:bg-muted focus-ring"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-6">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success/10 text-success">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </div>

          <div className="text-center">
            <h4 className="font-heading text-2xl font-bold text-foreground">Thank You!</h4>
            <p className="mt-1 text-sm text-muted-foreground">
              Your order has been placed successfully
            </p>
          </div>

          <div className="mt-6 space-y-3 rounded-xl border border-border bg-background px-5 py-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Order Number</span>
              <span className="font-semibold text-foreground">{orderNumber}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Estimated Time</span>
              <span className="font-semibold text-foreground">{estimatedTime}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">SMS Sent To</span>
              <span className="font-semibold text-foreground">{phoneNumber}</span>
            </div>
          </div>

          <p className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
            <span className="mt-0.5 inline-block h-4 w-4 rounded-full bg-warning/20 text-warning">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" x2="12" y1="8" y2="12" />
                <line x1="12" x2="12.01" y1="16" y2="16" />
              </svg>
            </span>
            You will receive SMS notifications for order confirmation, preparation updates, and
            delivery status
          </p>

          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-medium text-foreground transition-smooth hover:bg-muted focus-ring"
              onClick={backToMenu}
            >
              ← Back to Menu
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-medium text-primary-foreground shadow-md transition-smooth hover-lift focus-ring"
              onClick={goHome}
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
